import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  LinearProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { PlayArrow, OpenInNew } from "@mui/icons-material";
import dayjs from "dayjs";
import CommonPagination from "../../components/Table/CommonPagination";
import ExpandableText from "../../components/ExpandableText";
import VideoPlayerDialog from "../../components/VideoPlayerDialog";
import typeOutline from "../../assets/typeOutline.svg";
import { isVideoUrl, getPostThumbnail } from "../../utils/common";
import { getUserModificationPosts } from "../../http/userModification";

const PAGE_SIZE = 20;

const POST_TYPE_CONFIG = {
  MEDIA_POST: { bg: "#eceff1", color: "#37474f", label: "Media" },
  TEXT_POST: { bg: "#eceff1", color: "#37474f", label: "Text" },
  PROMPT_POST: { bg: "#e3f2fd", color: "#1976d2", label: "Prompt" },
  CATEGORY_POST: { bg: "#f3e5f5", color: "#7b1fa2", label: "Category" },
  CONTEST_POST: { bg: "#fff8e1", color: "#ff8f00", label: "Contest" },
  KARAOKE_POST: { bg: "#fce4ec", color: "#c2185b", label: "Karaoke" },
  DANCE_POST: { bg: "#ccfbf1", color: "#0f766e", label: "Dance" },
  ART_POST: { bg: "#fff3e0", color: "#e65100", label: "Art" },
  GROUP_DUET_POST: { bg: "#ede7f6", color: "#4527a0", label: "Group duet" },
  RAP_BATTLE_POST: { bg: "#e8f5e9", color: "#2e7d32", label: "Rap battle" },
};

const getPostTypeConfig = (type) =>
  POST_TYPE_CONFIG[type] || {
    bg: "#f5f5f5",
    color: "#757575",
    label: type || "Unknown",
  };

const headerCellStyle = {
  fontSize: "12px",
  color: "#000",
  fontWeight: 600,
  backgroundColor: "#e0e0e0",
  whiteSpace: "nowrap",
};

const bodyCellStyle = {
  fontSize: "12px",
  color: "#333",
  verticalAlign: "top",
};

function PostStatusChips({ post }) {
  const chips = [];
  if (post.isDeleted) {
    chips.push(
      <Chip
        key="deleted"
        size="small"
        label="Deleted"
        sx={{ bgcolor: "#9e9e9e", color: "#fff", fontSize: "11px" }}
      />,
    );
  } else if (post.isDisabled) {
    chips.push(
      <Chip
        key="disabled"
        size="small"
        label="Disabled"
        color="error"
        sx={{ fontSize: "11px" }}
      />,
    );
  } else {
    chips.push(
      <Chip
        key="active"
        size="small"
        label="Active"
        color="success"
        sx={{ fontSize: "11px" }}
      />,
    );
  }
  if (post.adminAction) {
    chips.push(
      <Chip
        key="admin"
        size="small"
        variant="outlined"
        label={`Admin: ${post.adminAction.toLowerCase()}`}
        sx={{ fontSize: "11px" }}
      />,
    );
  }
  if (post.commentsDisabled) {
    chips.push(
      <Chip
        key="comments"
        size="small"
        variant="outlined"
        label="Comments off"
        sx={{ fontSize: "11px" }}
      />,
    );
  }
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>{chips}</Box>
  );
}

function describeMatchProgress(post) {
  if (post.type === "PROMPT_POST" && post.matchStats) {
    const { wins = 0, losses = 0, ties = 0, isEliminated, isActive } =
      post.matchStats;
    const state = isEliminated
      ? "eliminated"
      : isActive
        ? "in match"
        : post.matchmakingStoppedAt
          ? "stopped"
          : post.promptPostStatus === "COMPLETED"
            ? "completed"
            : "searching";
    return `${wins}W / ${losses}L / ${ties}T · ${state}`;
  }

  if (post.type === "CONTEST_POST") {
    const mission =
      post.missionNumber != null ? `Mission ${post.missionNumber} · ` : "";
    return post.eliminated
      ? `${mission}Eliminated (round ${post.eliminatedRound || "—"})`
      : `${mission}In contest`;
  }

  if (post.matchStats && typeof post.matchStats.matchCount === "number") {
    const { matchCount, maxMatches, isCompleted, isActive } = post.matchStats;
    const state = isActive
      ? "in match"
      : isCompleted
        ? post.matchmakingStoppedAt
          ? "stopped"
          : "completed"
        : "searching";
    return `${matchCount}/${maxMatches ?? "?"} matches · ${state}`;
  }

  if (post.type === "RAP_BATTLE_POST" && post.rapBattle) {
    const { result, myVotes, opponentVotes, opponentUsername } = post.rapBattle;
    const votes =
      myVotes != null && opponentVotes != null
        ? ` (${myVotes}–${opponentVotes})`
        : "";
    return `${result || "—"}${votes}${
      opponentUsername ? ` vs @${opponentUsername}` : ""
    }`;
  }

  return "—";
}

function getPlayableSources(post) {
  const sources = [];
  if (isVideoUrl(post.media)) {
    sources.push({
      label: "Watch",
      url: post.media,
      fallbackUrl: post.originalMedia || "",
    });
  }
  // Unstitched group duets keep each member's clip in member1Media/member2Media.
  if (post.type === "GROUP_DUET_POST" && !sources.length) {
    if (isVideoUrl(post.member1Media)) {
      sources.push({
        label: post.member1 ? `@${post.member1}` : "Member 1",
        url: post.member1Media,
        fallbackUrl: "",
      });
    }
    if (isVideoUrl(post.member2Media)) {
      sources.push({
        label: post.member2 ? `@${post.member2}` : "Member 2",
        url: post.member2Media,
        fallbackUrl: "",
      });
    }
  }
  return sources;
}

const UserPostsTab = ({ username }) => {
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [videoTarget, setVideoTarget] = useState(null);

  // The parent remounts this tab per user (key={username}), so only the
  // filter toggle needs to reset paging.
  const handleIncludeDeletedChange = (checked) => {
    setIncludeDeleted(checked);
    setPage(1);
  };

  const fetchPosts = useCallback(async () => {
    if (!username) return;
    setLoading(true);
    setError("");
    try {
      const response = await getUserModificationPosts({
        username,
        page,
        limit: PAGE_SIZE,
        includeDeleted,
      });
      const payload = response?.data || response;
      setPosts(payload?.posts || []);
      setTotalCount(payload?.totalCount || 0);
    } catch (err) {
      setPosts([]);
      setTotalCount(0);
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.data?.message ||
          err?.message ||
          "Failed to load posts",
      );
    } finally {
      setLoading(false);
    }
  }, [username, page, includeDeleted]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const openImage = (url) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography sx={{ fontSize: "13px", color: "#666" }}>
          {totalCount} post{totalCount === 1 ? "" : "s"}
          {includeDeleted ? " (including deleted)" : ""}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeDeleted}
                onChange={(e) => handleIncludeDeletedChange(e.target.checked)}
              />
            }
            label={
              <Typography sx={{ fontSize: "13px" }}>Include deleted</Typography>
            }
          />
          <Button
            size="small"
            sx={{ textTransform: "none" }}
            onClick={fetchPosts}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 1 }} />}
      {error && (
        <Typography color="error" sx={{ fontSize: "13px", mb: 1 }}>
          {error}
        </Typography>
      )}

      <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellStyle}>Media</TableCell>
              <TableCell sx={headerCellStyle}>Type</TableCell>
              <TableCell sx={headerCellStyle}>Caption / context</TableCell>
              <TableCell sx={headerCellStyle}>Status</TableCell>
              <TableCell sx={headerCellStyle}>Matches</TableCell>
              <TableCell sx={headerCellStyle}>Likes</TableCell>
              <TableCell sx={headerCellStyle}>Comments</TableCell>
              <TableCell sx={headerCellStyle}>Posted</TableCell>
              <TableCell sx={headerCellStyle}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {posts.map((post) => {
              const typeConfig = getPostTypeConfig(post.type);
              const thumbnail = getPostThumbnail(post) || typeOutline;
              const sources = getPlayableSources(post);
              const primarySource = sources[0];
              const imageUrl =
                !sources.length && post.media && !isVideoUrl(post.media)
                  ? post.media
                  : null;
              const context = [
                post.contextLabel,
                post.type === "GROUP_DUET_POST" && (post.member1 || post.member2)
                  ? [post.member1, post.member2]
                      .filter(Boolean)
                      .map((m) => `@${m}`)
                      .join(" & ")
                  : null,
                post.convertedToCategoryPostAt ? "converted from prompt" : null,
              ]
                .filter(Boolean)
                .join(" · ");

              return (
                <TableRow key={post._id} sx={{ opacity: post.isDeleted ? 0.7 : 1 }}>
                  <TableCell sx={bodyCellStyle}>
                    <Box sx={{ position: "relative", width: 56, height: 56 }}>
                      <img
                        src={thumbnail}
                        alt=""
                        width="56"
                        height="56"
                        style={{
                          objectFit: "cover",
                          borderRadius: 4,
                          display: "block",
                          background: "#f0f0f0",
                        }}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = typeOutline;
                        }}
                      />
                      {primarySource && (
                        <Tooltip title="Play video" arrow>
                          <Box
                            onClick={() =>
                              setVideoTarget({ post, source: primarySource })
                            }
                            sx={{
                              position: "absolute",
                              inset: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "rgba(0,0,0,0.35)",
                              borderRadius: 1,
                              cursor: "pointer",
                              "&:hover": { backgroundColor: "rgba(0,0,0,0.55)" },
                            }}
                          >
                            <PlayArrow sx={{ color: "#fff" }} />
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={bodyCellStyle}>
                    <Chip
                      size="small"
                      label={typeConfig.label}
                      sx={{
                        backgroundColor: typeConfig.bg,
                        color: typeConfig.color,
                        fontWeight: 500,
                        fontSize: "11px",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ ...bodyCellStyle, maxWidth: 280 }}>
                    <ExpandableText
                      text={post.caption}
                      maxLines={3}
                      title="Post caption"
                      emptyLabel={
                        <em style={{ color: "#999" }}>No caption</em>
                      }
                      sx={{ fontSize: "12px", color: "#333" }}
                    />
                    {context && (
                      <Typography sx={{ fontSize: "11px", color: "#777", mt: 0.5 }}>
                        {context}
                      </Typography>
                    )}
                    {post.hashtags?.length > 0 && (
                      <Typography sx={{ fontSize: "11px", color: "#1976d2", mt: 0.25 }}>
                        {post.hashtags.map((tag) => `#${tag}`).join(" ")}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={bodyCellStyle}>
                    <PostStatusChips post={post} />
                  </TableCell>
                  <TableCell sx={{ ...bodyCellStyle, whiteSpace: "nowrap" }}>
                    {describeMatchProgress(post)}
                  </TableCell>
                  <TableCell sx={bodyCellStyle}>{post.likeCount ?? 0}</TableCell>
                  <TableCell sx={bodyCellStyle}>{post.commentCount ?? 0}</TableCell>
                  <TableCell sx={{ ...bodyCellStyle, whiteSpace: "nowrap" }}>
                    <Tooltip title={post._id} arrow>
                      <span>
                        {post.createdAt
                          ? dayjs(post.createdAt).format("MM/DD/YY HH:mm")
                          : "—"}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={bodyCellStyle}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {sources.map((source) => (
                        <Button
                          key={source.url}
                          size="small"
                          variant="outlined"
                          startIcon={<PlayArrow />}
                          sx={{ textTransform: "none", fontSize: "11px" }}
                          onClick={() => setVideoTarget({ post, source })}
                        >
                          {source.label}
                        </Button>
                      ))}
                      {imageUrl && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<OpenInNew />}
                          sx={{ textTransform: "none", fontSize: "11px" }}
                          onClick={() => openImage(imageUrl)}
                        >
                          Open image
                        </Button>
                      )}
                      {!sources.length && !imageUrl && (
                        <Typography sx={{ fontSize: "12px", color: "#888" }}>
                          {post.type === "TEXT_POST" ? "Text only" : "No media"}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {!posts.length && !loading && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: "#666" }}>
                  {error
                    ? "Could not load posts"
                    : includeDeleted
                      ? "No posts found"
                      : "No active posts — toggle “Include deleted” to see removed ones"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalCount > PAGE_SIZE && (
        <Box sx={{ mt: 1.5 }}>
          <CommonPagination
            rowsPerPage={PAGE_SIZE}
            count={totalCount}
            page={page - 1}
            onPageChange={(_, newPage) => setPage(newPage + 1)}
          />
        </Box>
      )}

      <VideoPlayerDialog
        open={Boolean(videoTarget)}
        onClose={() => setVideoTarget(null)}
        url={videoTarget?.source?.url || ""}
        fallbackUrl={videoTarget?.source?.fallbackUrl || ""}
        title={
          videoTarget
            ? `${getPostTypeConfig(videoTarget.post.type).label}${
                videoTarget.source.label && videoTarget.source.label !== "Watch"
                  ? ` · ${videoTarget.source.label}`
                  : ""
              }${videoTarget.post.caption ? ` — ${videoTarget.post.caption}` : ""}`
            : "Video"
        }
        isDisabled={Boolean(videoTarget?.post?.isDisabled || videoTarget?.post?.isDeleted)}
      />
    </Box>
  );
};

export default UserPostsTab;
